const React = require('react');
const { createElement: h, useState, useRef, useEffect } = React;
const ReactDOM = require('react-dom');
const { getSystemObjectTheme } = require('../lib/systemObjectTheme');
const NodeIcon = require('./NodeIcon');
const NodeTile = require('./NodeTile');
const { getNodeSubtitle } = require('../lib/nodeSubtitle');
const { getIncrementPreview } = require('../lib/incrementPreview');
const {
  OPERATOR_LABELS,
  conditionHasEffectiveValue,
  normalizeConditionRule,
  hasAnyConditionValue,
  operatorRequiresValue
} = require('../lib/conditionRule');
require('./WorkflowNode.css');

// Shared helper: build the `position` prop for PropPickerBalloon from a chip
// element's getBoundingClientRect(). Same shape used by every chip-style
// callsite — extracted once so we don't repeat the +8 gap math everywhere.
function pickerPositionFromRect(rect) {
  return { left: rect.right + 8, top: rect.top };
}

function StatIcon(props) {
  var type = props.type;
  var size = 14;
  var common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

  switch (type) {
    case 'gear':
      return h('svg', common,
        h('circle', { cx: 12, cy: 12, r: 3 }),
        h('path', { d: 'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z' })
      );
    case 'flag':
      return h('svg', common,
        h('path', { d: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z' }),
        h('line', { x1: 4, y1: 22, x2: 4, y2: 15 })
      );
    case 'check':
      return h('svg', common,
        h('path', { d: 'M22 11.08V12a10 10 0 11-5.93-9.14' }),
        h('polyline', { points: '22 4 12 14.01 9 11.01' })
      );
    case 'branch':
      return h('svg', common,
        h('line', { x1: 6, y1: 3, x2: 6, y2: 15 }),
        h('circle', { cx: 18, cy: 6, r: 3 }),
        h('circle', { cx: 6, cy: 18, r: 3 }),
        h('path', { d: 'M18 9a9 9 0 01-9 9' })
      );
    case 'eye':
      return h('svg', common,
        h('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
        h('circle', { cx: 12, cy: 12, r: 3 })
      );
    case 'error':
      return h('svg', common,
        h('circle', { cx: 12, cy: 12, r: 10 }),
        h('line', { x1: 15, y1: 9, x2: 9, y2: 15 }),
        h('line', { x1: 9, y1: 9, x2: 15, y2: 15 })
      );
    default:
      return null;
  }
}

function normalizeVariableType(value) {
  var raw = String(value || '').toLowerCase();
  if (raw === 'int' || raw === 'integer' || raw === 'number') return 'int';
  if (raw === 'date') return 'date';
  if (raw === 'bool' || raw === 'boolean') return 'bool';
  if (raw === 'json' || raw === 'object') return 'json';
  if (raw === 'array') return 'array';
  return 'string';
}

function VariableEditor(props) {
  var paramValues = props.paramValues || {};
  var onParamChange = props.onParamChange || function() {};
  var runtimeValue = props.runtimeValue;
  var hasRuntime = runtimeValue !== undefined && runtimeValue !== null;
  var [isFlashing, setIsFlashing] = useState(false);
  var prevRuntimeRef = useRef(runtimeValue);

  useEffect(function() {
    if (hasRuntime && runtimeValue !== prevRuntimeRef.current) {
      setIsFlashing(true);
      var t = setTimeout(function() { setIsFlashing(false); }, 600);
      prevRuntimeRef.current = runtimeValue;
      return function() { clearTimeout(t); };
    }
    prevRuntimeRef.current = runtimeValue;
  }, [runtimeValue]);

  var dataType = normalizeVariableType(paramValues.dataType);
  var isStatic = String(paramValues.retain || 'false') === 'true';
  var rawValue = paramValues.value;
  function toDisplayString(v) {
    if (v === undefined || v === null) return '';
    if (typeof v === 'object') { try { return JSON.stringify(v, null, 2); } catch(e) { return ''; } }
    return String(v);
  }
  var valueString = toDisplayString(rawValue);
  var boolValue = String(rawValue || '').toLowerCase() === 'true';
  var runtimeBoolValue = hasRuntime ? String(runtimeValue).toLowerCase() === 'true' : boolValue;
  var runtimeString = hasRuntime ? toDisplayString(runtimeValue) : valueString;

  function onDataTypeChange(nextType) {
    var normalizedType = normalizeVariableType(nextType);
    onParamChange('dataType', normalizedType);
    // Only set a default when the current value is missing or incompatible
    var cur = String(paramValues.value || '');
    if (normalizedType === 'bool') {
      if (cur !== 'true' && cur !== 'false') onParamChange('value', 'false');
    } else if (normalizedType === 'json' && !cur) {
      onParamChange('value', '{}');
    } else if (normalizedType === 'array' && !cur) {
      onParamChange('value', '[]');
    }
    // All other type switches: keep existing value as-is
  }

  var isMultiline = dataType === 'json' || dataType === 'array';
  var [showExpand, setShowExpand] = useState(false);

  return h('div', { className: 'wf-node-variable-editor' },
    showExpand && h('div', {
      className: 'wf-node-variable-expand-overlay',
      onMouseDown: function(e) { e.stopPropagation(); },
      onClick: function(e) { if (e.target === e.currentTarget) setShowExpand(false); }
    },
      h('div', { className: 'wf-node-variable-expand-modal' },
        h('div', { className: 'wf-node-variable-expand-header' },
          h('span', null, 'Edit value'),
          h('button', { type: 'button', className: 'wf-node-variable-expand-close', onClick: function() { setShowExpand(false); } }, '\u00d7')
        ),
        h('textarea', {
          className: 'wf-node-variable-expand-textarea',
          value: valueString,
          placeholder: 'value',
          autoFocus: true,
          onChange: function(e) { onParamChange('value', e.target.value); },
          onMouseDown: function(e) { e.stopPropagation(); }
        })
      )
    ),
    h('div', { className: 'wf-node-variable-row' + (isMultiline ? ' wf-node-variable-row-compact' : '') },
      h('label', { className: 'wf-node-variable-field' },
        h('span', { className: 'wf-node-variable-label' }, 'Data type'),
        h('select', {
          className: 'wf-node-variable-input',
          value: dataType,
          onChange: function(e) { onDataTypeChange(e.target.value); },
          onMouseDown: function(e) { e.stopPropagation(); }
        },
          h('option', { value: 'string' }, 'Text'),
          h('option', { value: 'int' }, 'Number'),
          h('option', { value: 'date' }, 'Date'),
          h('option', { value: 'bool' }, 'True / False'),
          h('option', { value: 'json' }, 'Object'),
          h('option', { value: 'array' }, 'List')
        )
      ),
      h('label', { className: 'wf-node-variable-field' },
        h('span', { className: 'wf-node-variable-label', style: { display: 'flex', alignItems: 'center' } },
          hasRuntime ? 'Value \u25cf' : 'Value',
          dataType === 'string'
            ? h('button', {
                type: 'button',
                className: 'wf-node-variable-expand-btn',
                title: 'Expand editor',
                onMouseDown: function(e) { e.stopPropagation(); },
                onClick: function(e) { e.stopPropagation(); setShowExpand(true); }
              }, '\u26f6')
            : null
        ),
        dataType === 'bool'
          ? h('button', {
              className: 'wf-node-variable-switch' + (boolValue ? ' wf-node-variable-switch-on' : '') + (hasRuntime ? ' wf-node-variable-switch-live' : '') + (isFlashing ? ' wf-node-variable-flash' : ''),
              type: 'button',
              onMouseDown: function(e) {
                e.preventDefault();
                e.stopPropagation();
                onParamChange('value', boolValue ? 'false' : 'true');
              },
              title: 'Toggle boolean'
            },
              h('span', { className: 'wf-node-variable-switch-track' },
                h('span', { className: 'wf-node-variable-switch-thumb' })
              ),
              h('span', { className: 'wf-node-variable-switch-text' }, boolValue ? 'True' : 'False')
            )
          : (dataType === 'json' || dataType === 'array')
          ? h('textarea', {
              className: 'wf-node-variable-textarea' + (hasRuntime ? ' wf-node-variable-input-live' : '') + (isFlashing ? ' wf-node-variable-flash' : ''),
              placeholder: dataType === 'array' ? '[]' : '{}',
              value: valueString,
              rows: 3,
              onChange: function(e) { onParamChange('value', e.target.value); },
              onMouseDown: function(e) { e.stopPropagation(); }
            })
          : h('input', {
              className: 'wf-node-variable-input' + (hasRuntime ? ' wf-node-variable-input-live' : '') + (isFlashing ? ' wf-node-variable-flash' : ''),
              type: dataType === 'date' ? 'date' : (dataType === 'int' ? 'number' : 'text'),
              step: dataType === 'int' ? '1' : undefined,
              placeholder: dataType === 'date' ? 'YYYY-MM-DD' : (dataType === 'int' ? '0' : 'value'),
              value: valueString,
              onChange: function(e) { onParamChange('value', e.target.value); },
              onMouseDown: function(e) { e.stopPropagation(); }
            })
      )
    ),
    h('div', { className: 'wf-node-variable-row wf-node-variable-row-compact' },
      h('label', { className: 'wf-node-variable-field wf-node-variable-field-full' },
        h('span', { className: 'wf-node-variable-label' }, 'Mode'),
        h('span', { className: 'wf-node-variable-mode-toggle' },
          h('button', {
            type: 'button',
            className: 'wf-node-variable-mode-btn' + (!isStatic ? ' wf-node-variable-mode-btn-active' : ''),
            onMouseDown: function(e) {
              e.preventDefault();
              e.stopPropagation();
              onParamChange('retain', 'false');
            },
            title: 'Variable changes stay only in the current run'
          }, 'Temporary'),
          h('button', {
            type: 'button',
            className: 'wf-node-variable-mode-btn' + (isStatic ? ' wf-node-variable-mode-btn-active' : ''),
            onMouseDown: function(e) {
              e.preventDefault();
              e.stopPropagation();
              onParamChange('retain', 'true');
            },
            title: 'Keep the latest value for the next run'
          }, 'Persistent')
        )
      )
    )
  );
}

function FilePreviewDisplay(props) {
  var url = typeof props.runtimeOutput === 'string' ? props.runtimeOutput.trim() : null;
  if (!url) return null;
  var lower = url.toLowerCase().split('?')[0].split('#')[0];
  var isImage = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/.test(lower);
  var isPdf = /\.pdf$/.test(lower);
  var fileName = lower.split('/').pop() || 'file';

  var downloadBtn = h('a', {
    className: 'wf-node-file-download-btn',
    href: url,
    target: '_blank',
    rel: 'noopener noreferrer',
    onMouseDown: function(e) { e.stopPropagation(); },
    title: 'Open ' + fileName
  },
    h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
      h('path', { d: 'M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6' }),
      h('polyline', { points: '15 3 21 3 21 9' }),
      h('line', { x1: 10, y1: 14, x2: 21, y2: 3 })
    ),
    'Open'
  );

  if (!isImage && !isPdf) {
    return h('div', { className: 'wf-node-file-preview wf-node-file-preview-nopreview' },
      downloadBtn
    );
  }

  if (isImage) {
    return h('div', { className: 'wf-node-file-preview' },
      h('img', { src: url, className: 'wf-node-file-preview-img', alt: 'preview' }),
      h('div', { className: 'wf-node-file-preview-footer' }, downloadBtn)
    );
  }

  // PDF — #toolbar=0 hides the browser's built-in download/print toolbar
  var pdfSrc = url + (url.indexOf('#') === -1 ? '#' : '&') + 'toolbar=0&navpanes=0&scrollbar=0';
  return h('div', { className: 'wf-node-file-preview' },
    h('div', { className: 'wf-node-file-preview-wrap' },
      h('iframe', {
        src: pdfSrc,
        className: 'wf-node-file-preview-frame',
        title: 'File Preview',
        sandbox: 'allow-scripts allow-same-origin allow-forms'
      })
    ),
    h('div', { className: 'wf-node-file-preview-footer' }, downloadBtn)
  );
}

function ConsoleLogOutput(props) {
  var paramValues = props.paramValues || {};
  var runtimeOutput = props.runtimeOutput !== undefined && props.runtimeOutput !== null
    ? String(props.runtimeOutput)
    : null;
  var inputValue = runtimeOutput !== null
    ? runtimeOutput
    : paramValues.input !== undefined && paramValues.input !== null
    ? String(paramValues.input)
    : (paramValues.value !== undefined && paramValues.value !== null ? String(paramValues.value) : '');

  return h('div', { className: 'wf-node-console-output' },
    h('span', { className: 'wf-node-console-label' }, 'Output'),
    h('div', { className: 'wf-node-console-value' }, inputValue || null)
  );
}

function SetVariableEditor(props) {
  var paramValues = props.paramValues || {};
  var onParamChange = props.onParamChange || function() {};
  var variableNodes = props.variableNodes || [];
  var selectedVar = String(paramValues.variable || '');
  var isFromInput = String(paramValues.source || 'false') === 'true';
  var selectedMode = String(paramValues.mode || 'replace').toLowerCase();
  var manualValue = String(paramValues.value !== undefined && paramValues.value !== null ? paramValues.value : '');

  var [editing, setEditing] = useState(false);
  var [draft, setDraft] = useState('');
  var [selectedIdx, setSelectedIdx] = useState(0);
  var [fieldSelectorVn, setFieldSelectorVn] = useState(null);
  var [fieldSelectorPos, setFieldSelectorPos] = useState(null);
  var inputRef = useRef(null);
  var draftRef = useRef(draft);
  draftRef.current = draft;

  // Build option entries with stable, unique keys. Using just the title as
  // the key produced React duplicate-key warnings (2k+ per session) when the
  // canvas had two nodes sharing a title — e.g. two set-variable cards
  // pointing at the same target. The id is unique even when the display
  // name isn't, so key on id and dedupe by name for the actual options shown.
  var seenNames = {};
  var varOptions = [];
  for (var __vi = 0; __vi < variableNodes.length; __vi += 1) {
    var __vn = variableNodes[__vi];
    var __name = String(__vn && __vn.title || '').trim();
    if (!__name) continue;
    if (seenNames[__name]) continue;
    seenNames[__name] = true;
    varOptions.push({ id: String(__vn.id || __name), name: __name });
  }
  var varNames = varOptions.map(function(o) { return o.name; });

  // Compute @-reference suggestions when editing
  var suggestions = [];
  var showDropdown = false;
  if (editing && typeof draft === 'string') {
    var atIdx = draft.lastIndexOf('@');
    var query = atIdx >= 0 ? draft.slice(atIdx + 1).toLowerCase() : '';
    suggestions = variableNodes.filter(function(vn) {
      var name = String(vn.title || '').trim();
      if (!name.length) return false;
      return atIdx >= 0 ? name.toLowerCase().indexOf(query) >= 0 : true;
    });
    showDropdown = atIdx >= 0 && suggestions.length > 0;
  }

  function selectVariable(vn, selectedPath) {
    var name = String(vn.title || '').trim();
    var suffix = selectedPath ? '.' + selectedPath : '';
    var refValue = '@' + name + suffix;
    var atIdx = draftRef.current.lastIndexOf('@');
    var newDraft = atIdx >= 0 ? draftRef.current.slice(0, atIdx) + refValue : refValue;
    setDraft(newDraft);
    setSelectedIdx(0);
    setFieldSelectorVn(null);
    setFieldSelectorPos(null);
    onParamChange('value', newDraft);
    setEditing(false);
  }

  function startEdit(e) {
    e.stopPropagation();
    setEditing(true);
    setDraft(manualValue);
    setSelectedIdx(0);
    setTimeout(function() { if (inputRef.current) inputRef.current.focus(); }, 0);
  }

  function commitEdit() {
    onParamChange('value', draftRef.current);
    setEditing(false);
    setFieldSelectorVn(null);
    setFieldSelectorPos(null);
    setSelectedIdx(0);
  }

  function handleKeyDown(e) {
    e.stopPropagation();
    if (fieldSelectorVn) {
      if (e.key === 'Escape') { e.preventDefault(); setFieldSelectorVn(null); setFieldSelectorPos(null); }
      return;
    }
    if (showDropdown) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(function(p) { return Math.min(p + 1, suggestions.length - 1); }); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(function(p) { return Math.max(p - 1, 0); }); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectVariable(suggestions[selectedIdx]); return; }
    }
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') { setEditing(false); setSelectedIdx(0); }
  }

  // Render the value chip
  var valueChip = null;
  if (!isFromInput) {
    if (editing) {
      valueChip = h('span', { className: 'wf-node-param-chip wf-node-param-chip-editing', style: { position: 'relative' } },
        h('span', { className: 'wf-node-param-key' }, 'value'),
        h('span', { className: 'wf-node-param-sep' }, '='),
        h('input', {
          ref: inputRef,
          className: 'wf-node-setvar-input',
          type: 'text',
          value: draft,
          placeholder: 'value or @variable',
          onChange: function(e) { setDraft(e.target.value); setSelectedIdx(0); },
          onBlur: function() { setTimeout(commitEdit, 120); },
          onKeyDown: handleKeyDown,
          onMouseDown: function(e) { e.stopPropagation(); }
        }),
        showDropdown && !fieldSelectorVn
          ? h('div', { className: 'wf-var-dropdown', onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); } },
              (function() {
                var varItems = suggestions.filter(function(vn) { return !vn._isNodeOutput; });
                var outItems = suggestions.filter(function(vn) { return vn._isNodeOutput; });
                var items = [];
                var si = 0;
                if (varItems.length) {
                  items.push(h('div', { key: '__hdr-var', className: 'wf-var-dropdown-section' }, 'Variables'));
                  varItems.forEach(function(vn) {
                    var idx = si++;
                    var varParams = vn.paramValues || {};
                    var varType = VAR_TYPE_SHORT[normalizeVariableType(varParams.dataType)] || 'str';
                    var runtimeOut = vn._runtimeOutput;
                    var hasComplex = runtimeOut !== null && runtimeOut !== undefined && typeof runtimeOut === 'object';
                    var summary = hasComplex
                      ? (Array.isArray(runtimeOut) ? runtimeOut.length + ' items' : Object.keys(runtimeOut).length + ' fields')
                      : '';
                    items.push(h('div', {
                      key: vn.id, className: 'wf-var-dropdown-item' + (idx === selectedIdx ? ' wf-var-dropdown-item-active' : ''),
                      onMouseDown: function(e) {
                        e.preventDefault(); e.stopPropagation();
                        if (hasComplex) {
                          var rect = e.currentTarget.getBoundingClientRect();
                          setFieldSelectorPos(pickerPositionFromRect(rect));
                          setFieldSelectorVn(vn);
                        } else { selectVariable(vn); }
                      }
                    },
                      h('span', { className: 'wf-var-dropdown-at' }, '@'),
                      h('span', { className: 'wf-var-dropdown-name' }, vn.title),
                      summary ? h('span', { className: 'wf-var-dropdown-meta' }, summary) : null,
                      h('span', { className: 'wf-var-dropdown-type' + (hasComplex ? ' wf-var-dropdown-type-out' : '') }, hasComplex ? 'data' : varType),
                      hasComplex ? h('span', { className: 'wf-var-dropdown-expand' }, '\u203a') : null
                    ));
                  });
                }
                if (outItems.length) {
                  items.push(h('div', { key: '__hdr-out', className: 'wf-var-dropdown-section' + (varItems.length ? ' wf-var-dropdown-section-border' : '') }, 'From previous steps'));
                  outItems.forEach(function(vn) {
                    var idx = si++;
                    var outputValue = vn._runtimeOutput != null ? vn._runtimeOutput : null;
                    var hasComplex = outputValue !== null && typeof outputValue === 'object';
                    var summary = hasComplex
                      ? (Array.isArray(outputValue) ? outputValue.length + ' items' : Object.keys(outputValue).length + ' fields')
                      : '';
                    items.push(h('div', {
                      key: vn.id, className: 'wf-var-dropdown-item' + (idx === selectedIdx ? ' wf-var-dropdown-item-active' : ''),
                      onMouseDown: function(e) {
                        e.preventDefault(); e.stopPropagation();
                        if (hasComplex) {
                          var rect = e.currentTarget.getBoundingClientRect();
                          setFieldSelectorPos(pickerPositionFromRect(rect));
                          setFieldSelectorVn(vn);
                        } else { selectVariable(vn); }
                      }
                    },
                      h('span', { className: 'wf-var-dropdown-at' }, '@'),
                      h('span', { className: 'wf-var-dropdown-name' }, vn.title),
                      summary ? h('span', { className: 'wf-var-dropdown-meta' }, summary) : null,
                      h('span', { className: 'wf-var-dropdown-type wf-var-dropdown-type-out' }, hasComplex ? 'data' : 'value'),
                      hasComplex ? h('span', { className: 'wf-var-dropdown-expand' }, '\u203a') : null
                    ));
                  });
                }
                return items;
              })()
            )
          : null,
        fieldSelectorVn
          ? h(FieldSelectorPanel, {
              vn: fieldSelectorVn,
              onSelect: function(path) { selectVariable(fieldSelectorVn, path); },
              onBack: function() { setFieldSelectorVn(null); setFieldSelectorPos(null); }
            })
          : null
      );
    } else {
      var isVarRef = manualValue.indexOf('@') === 0 && manualValue.length > 1;
      valueChip = h('button', {
        className: 'wf-node-param-chip' + (isVarRef ? ' wf-node-param-chip-var' : ''),
        onMouseDown: startEdit
      },
        h('span', { className: 'wf-node-param-key' }, 'value'),
        h('span', { className: 'wf-node-param-sep' }, '='),
        h('span', { className: 'wf-node-param-val' }, manualValue || '...')
      );
    }
  }

  return h('div', { className: 'wf-node-setvar-editor' },
    h('span', { className: 'wf-node-param-chip' },
      h('span', { className: 'wf-node-param-key' }, 'var'),
      h('span', { className: 'wf-node-param-sep' }, '='),
      h('select', {
        className: 'wf-node-setvar-select',
        value: selectedVar,
        onChange: function(e) { onParamChange('variable', e.target.value); },
        onMouseDown: function(e) { e.stopPropagation(); }
      },
        h('option', { key: '__empty', value: '' }, '...'),
        varOptions.map(function(opt) {
          return h('option', { key: opt.id, value: opt.name }, opt.name);
        })
      )
    ),
    h('span', { className: 'wf-node-param-chip' },
      h('span', { className: 'wf-node-param-key' }, 'from'),
      h('span', { className: 'wf-node-param-sep' }, '='),
      h('span', { className: 'wf-node-setvar-toggle' },
        h('button', {
          className: 'wf-node-setvar-toggle-btn' + (!isFromInput ? ' wf-node-setvar-toggle-active' : ''),
          onMouseDown: function(e) { e.stopPropagation(); e.preventDefault(); onParamChange('source', 'false'); }
        }, 'manual'),
        h('button', {
          className: 'wf-node-setvar-toggle-btn' + (isFromInput ? ' wf-node-setvar-toggle-active' : ''),
          onMouseDown: function(e) { e.stopPropagation(); e.preventDefault(); onParamChange('source', 'true'); }
        }, 'input')
      )
    ),
    selectedMode !== 'replace'
      ? h('span', { className: 'wf-node-param-chip wf-node-param-chip-var' },
          h('span', { className: 'wf-node-param-key' }, 'mode'),
          h('span', { className: 'wf-node-param-sep' }, '='),
          h('span', { className: 'wf-node-param-val' }, selectedMode)
        )
      : null,
    valueChip
  );
}

var AI_TIER_ORDER = ['fast', 'balance', 'thorough'];
// The Tool Agent runs a multi-step tool-calling loop that needs reliable
// structured-tag output — gpt-5.4-mini (fast) is too weak for it, so its tier
// badge cycles only Haiku/Sonnet. Other AI nodes keep all three tiers.
var TOOL_AGENT_TIER_ORDER = ['balance', 'thorough'];
var AI_TIER_TITLES = {
  fast: 'Fast — lowest cost, good for simple tasks — click to cycle',
  balance: 'Balanced — best mix of cost and quality — click to cycle',
  thorough: 'Thorough — highest quality, higher cost — click to cycle'
};

function aiTierIcon(tier) {
  if (tier === 'fast') {
    // lightning bolt
    return h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'currentColor' },
      h('path', { d: 'M13 2 4 14h7l-1 8 9-12h-7l1-8z' })
    );
  }
  if (tier === 'balance') {
    // scales / balance
    return h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
      h('path', { d: 'M12 3v18M5 21h14M6 8l-3 6a4 4 0 0 0 6 0l-3-6zm12 0-3 6a4 4 0 0 0 6 0l-3-6z' })
    );
  }
  // thorough — brain (deep thinking)
  return h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2z' }),
    h('path', { d: 'M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2z' })
  );
}

function AiTierCycleButton(props) {
  var paramValues = props.paramValues || {};
  var onParamChange = props.onParamChange || function() {};
  var order = props.toolAgent ? TOOL_AGENT_TIER_ORDER : AI_TIER_ORDER;
  // Default to the first allowed tier (Tool Agent => 'balance', others => 'fast'),
  // and coerce a now-disallowed saved value (e.g. legacy 'fast' on a Tool Agent)
  // up to the first allowed tier.
  var current = order.indexOf(paramValues.aiTier) >= 0 ? paramValues.aiTier : order[0];
  function cycle(event) {
    event.preventDefault();
    event.stopPropagation();
    var idx = order.indexOf(current);
    var next = order[(idx + 1) % order.length];
    onParamChange('aiTier', next);
  }
  return h('button', {
    className: 'wf-node-ai-tier wf-node-ai-tier-' + current,
    onMouseDown: cycle,
    onTouchEnd: cycle,
    title: AI_TIER_TITLES[current]
  }, aiTierIcon(current));
}

function AgentSummary(props) {
  var paramValues = props.paramValues || {};
  var system = String(paramValues.system || 'You are a helpful AI assistant.').trim();
  var variableInput = String(paramValues.variableInput || '').trim();
  var temperature = paramValues.temperature !== undefined ? paramValues.temperature : 0.7;
  var preview = system.length > 50 ? system.slice(0, 50) + '...' : system;

  return h('div', { className: 'wf-node-agent-summary' },
    h('div', { className: 'wf-node-agent-summary-row' },
      h('span', { className: 'wf-node-agent-summary-label' }, 'System'),
      h('span', { className: 'wf-node-agent-summary-value' }, preview)
    ),
    variableInput
      ? h('div', { className: 'wf-node-agent-summary-row' },
          h('span', { className: 'wf-node-agent-summary-label' }, 'Input'),
          h('span', { className: 'wf-node-agent-summary-value' }, variableInput.length > 30 ? variableInput.slice(0, 30) + '...' : variableInput)
        )
      : null,
    h('div', { className: 'wf-node-agent-summary-row' },
      h('span', { className: 'wf-node-agent-summary-chip' }, 'creativity=' + temperature)
    )
  );
}

function ClassifierSummary(props) {
  var paramValues = props.paramValues || {};
  var runtimeOutput = props.runtimeOutput;
  var onParamChange = props.onParamChange || function() {};
  var labels = String(paramValues.labels || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
  var instruction = String(paramValues.instruction || '').trim();
  var confidenceMap = runtimeOutput && typeof runtimeOutput === 'object' && runtimeOutput.confidences && typeof runtimeOutput.confidences === 'object'
    ? runtimeOutput.confidences
    : null;

  var [editingIndex, setEditingIndex] = useState(null);
  var [editDraft, setEditDraft] = useState('');
  var [addingNew, setAddingNew] = useState(false);
  var [newDraft, setNewDraft] = useState('');
  var editRef = useRef(null);
  var addRef = useRef(null);

  useEffect(function() {
    if (editingIndex !== null && editRef.current) editRef.current.focus();
  }, [editingIndex]);

  useEffect(function() {
    if (addingNew && addRef.current) addRef.current.focus();
  }, [addingNew]);

  function commitRename(index) {
    var trimmed = editDraft.trim();
    setEditingIndex(null);
    if (!trimmed || trimmed === labels[index]) return;
    var next = labels.slice();
    next[index] = trimmed;
    onParamChange('labels', next.join(','));
  }

  function removeLabel(index) {
    var next = labels.filter(function(_, i) { return i !== index; });
    onParamChange('labels', next.join(','));
  }

  function commitAdd() {
    var trimmed = newDraft.trim();
    setAddingNew(false);
    setNewDraft('');
    if (!trimmed) return;
    if (labels.indexOf(trimmed) >= 0) return;
    onParamChange('labels', labels.concat(trimmed).join(','));
  }

  return h('div', { className: 'wf-node-classifier-summary' },
    labels.length
      ? h('div', { className: 'wf-node-classifier-list' },
          labels.map(function(label, i) {
            var confidence = confidenceMap && Number.isFinite(Number(confidenceMap[label]))
              ? Math.max(0, Math.min(100, Number(confidenceMap[label])))
              : 0;
            var isActive = runtimeOutput && runtimeOutput.classification === label;
            var isEditing = editingIndex === i;

            return h('div', { key: label, className: 'wf-node-classifier-row' + (isActive ? ' wf-node-classifier-row-active' : '') },
              confidenceMap
                ? h('div', {
                    className: 'wf-node-classifier-fill',
                    style: { width: confidence + '%' }
                  })
                : null,
              isEditing
                ? h('input', {
                    ref: editRef,
                    className: 'wf-node-classifier-edit-input',
                    value: editDraft,
                    onChange: function(e) { setEditDraft(e.target.value); },
                    onKeyDown: function(e) {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(i); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingIndex(null); }
                    },
                    onBlur: function() { commitRename(i); },
                    onMouseDown: function(e) { e.stopPropagation(); },
                    onClick: function(e) { e.stopPropagation(); }
                  })
                : h('span', {
                    className: 'wf-node-classifier-text',
                    onDoubleClick: function(e) {
                      e.stopPropagation();
                      e.preventDefault();
                      setEditingIndex(i);
                      setEditDraft(label);
                    }
                  }, label),
              confidenceMap
                ? h('span', { className: 'wf-node-classifier-pct' }, confidence + '%')
                : null,
              !isEditing
                ? h('button', {
                    className: 'wf-node-classifier-remove',
                    onClick: function(e) { e.stopPropagation(); removeLabel(i); },
                    onMouseDown: function(e) { e.stopPropagation(); },
                    title: 'Remove label'
                  }, '\u00d7')
                : null
            );
          })
        )
      : h('div', { className: 'wf-node-classifier-empty' }, 'No labels'),
    addingNew
      ? h('div', { className: 'wf-node-classifier-row wf-node-classifier-row-add' },
          h('input', {
            ref: addRef,
            className: 'wf-node-classifier-edit-input',
            placeholder: 'Label name...',
            value: newDraft,
            onChange: function(e) { setNewDraft(e.target.value); },
            onKeyDown: function(e) {
              if (e.key === 'Enter') { e.preventDefault(); commitAdd(); }
              if (e.key === 'Escape') { e.preventDefault(); setAddingNew(false); setNewDraft(''); }
            },
            onBlur: function() { commitAdd(); },
            onMouseDown: function(e) { e.stopPropagation(); },
            onClick: function(e) { e.stopPropagation(); }
          })
        )
      : h('button', {
          className: 'wf-node-classifier-add-btn',
          onClick: function(e) { e.stopPropagation(); setAddingNew(true); },
          onMouseDown: function(e) { e.stopPropagation(); }
        }, '+ Add label'),
    instruction
      ? h('div', { className: 'wf-node-classifier-instr' }, instruction.length > 40 ? instruction.slice(0, 40) + '...' : instruction)
      : null
  );
}

var ROUTER_INPUT_PARAM = [{ key: 'input', type: 'any', default: '' }];

function RouterSummary(props) {
  var paramValues = props.paramValues || {};
  var runtimeOutput = props.runtimeOutput;
  var onParamChange = props.onParamChange || function() {};
  var variableNodes = props.variableNodes || [];
  var labels = String(paramValues.labels || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);

  var [editingIndex, setEditingIndex] = useState(null);
  var [editDraft, setEditDraft] = useState('');
  var [addingNew, setAddingNew] = useState(false);
  var [newDraft, setNewDraft] = useState('');
  var editRef = useRef(null);
  var addRef = useRef(null);

  useEffect(function() {
    if (editingIndex !== null && editRef.current) editRef.current.focus();
  }, [editingIndex]);

  useEffect(function() {
    if (addingNew && addRef.current) addRef.current.focus();
  }, [addingNew]);

  function commitRename(index) {
    var trimmed = editDraft.trim();
    setEditingIndex(null);
    if (!trimmed || trimmed === labels[index]) return;
    var next = labels.slice();
    next[index] = trimmed;
    onParamChange('labels', next.join(','));
  }

  function removeLabel(index) {
    var next = labels.filter(function(_, i) { return i !== index; });
    onParamChange('labels', next.join(','));
  }

  function commitAdd() {
    var trimmed = newDraft.trim();
    setAddingNew(false);
    setNewDraft('');
    if (!trimmed) return;
    if (labels.indexOf(trimmed) >= 0) return;
    onParamChange('labels', labels.concat(trimmed).join(','));
  }

  var matchedRoute = runtimeOutput && typeof runtimeOutput === 'object' ? runtimeOutput.matched : null;

  return h('div', { className: 'wf-node-classifier-summary' },
    h(HyperParamChips, {
      params: ROUTER_INPUT_PARAM,
      paramValues: paramValues,
      onParamChange: onParamChange,
      variableNodes: variableNodes
    }),
    labels.length
      ? h('div', { className: 'wf-node-classifier-list' },
          labels.map(function(label, i) {
            var isActive = matchedRoute === label;
            var isEditing = editingIndex === i;

            return h('div', { key: label, className: 'wf-node-classifier-row' + (isActive ? ' wf-node-classifier-row-active' : '') },
              isEditing
                ? h('input', {
                    ref: editRef,
                    className: 'wf-node-classifier-edit-input',
                    value: editDraft,
                    onChange: function(e) { setEditDraft(e.target.value); },
                    onKeyDown: function(e) {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(i); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingIndex(null); }
                    },
                    onBlur: function() { commitRename(i); },
                    onMouseDown: function(e) { e.stopPropagation(); },
                    onClick: function(e) { e.stopPropagation(); }
                  })
                : h('span', {
                    className: 'wf-node-classifier-text',
                    onDoubleClick: function(e) {
                      e.stopPropagation();
                      e.preventDefault();
                      setEditingIndex(i);
                      setEditDraft(label);
                    }
                  }, label),
              !isEditing
                ? h('button', {
                    className: 'wf-node-classifier-remove',
                    onClick: function(e) { e.stopPropagation(); removeLabel(i); },
                    onMouseDown: function(e) { e.stopPropagation(); },
                    title: 'Remove route'
                  }, '\u00d7')
                : null
            );
          })
        )
      : h('div', { className: 'wf-node-classifier-empty' }, 'No routes'),
    addingNew
      ? h('div', { className: 'wf-node-classifier-row wf-node-classifier-row-add' },
          h('input', {
            ref: addRef,
            className: 'wf-node-classifier-edit-input',
            placeholder: 'Route name...',
            value: newDraft,
            onChange: function(e) { setNewDraft(e.target.value); },
            onKeyDown: function(e) {
              if (e.key === 'Enter') { e.preventDefault(); commitAdd(); }
              if (e.key === 'Escape') { e.preventDefault(); setAddingNew(false); setNewDraft(''); }
            },
            onBlur: function() { commitAdd(); },
            onMouseDown: function(e) { e.stopPropagation(); },
            onClick: function(e) { e.stopPropagation(); }
          })
        )
      : h('button', {
          className: 'wf-node-classifier-add-btn',
          onClick: function(e) { e.stopPropagation(); setAddingNew(true); },
          onMouseDown: function(e) { e.stopPropagation(); }
        }, '+ Add route')
  );
}

var TYPE_SHORT = { string: 'str', number: 'num', boolean: 'bool', datetime: 'date', array: 'arr' };
var VAR_TYPE_SHORT = { string: 'str', int: 'int', date: 'date', bool: 'bool' };

function summarizeVal(val) {
  if (val === null) return 'null';
  if (val === undefined) return '\u2014';
  if (Array.isArray(val)) return '[' + val.length + ']';
  if (typeof val === 'object') return '{' + Object.keys(val).length + '}';
  var s = String(val);
  return s.length > 22 ? s.slice(0, 22) + '\u2026' : s;
}

function getNodeTreeLabel(label, isArr) {
  if (label == null) return isArr ? 'List' : 'Details';
  if (isArr && /^\d+$/.test(String(label))) return 'Item ' + (Number(label) + 1);
  return label;
}

function getNodeTreeSummary(isArr, count) {
  return isArr
    ? count + ' ' + (count === 1 ? 'item' : 'items')
    : count + ' ' + (count === 1 ? 'field' : 'fields');
}

function getAutoExpandedPickerRoot(value) {
  var current = value;
  var pathPrefix = '';
  var safety = 0;

  while (current && typeof current === 'object' && !Array.isArray(current) && safety < 5) {
    var keys = Object.keys(current);
    if (keys.length !== 1) break;
    var onlyKey = keys[0];
    var nextValue = current[onlyKey];
    if (!nextValue || typeof nextValue !== 'object') break;
    pathPrefix = pathPrefix ? pathPrefix + '.' + onlyKey : onlyKey;
    current = nextValue;
    safety += 1;
  }

  return { value: current, pathPrefix: pathPrefix };
}

function NodePropPickerNode(props) {
  var label = props.label;
  var value = props.value;
  var path = props.path;
  var depth = props.depth || 0;
  var onSelect = props.onSelect;
  var [open, setOpen] = useState(depth < 2);

  var isObj = value !== null && value !== undefined && typeof value === 'object';
  var isArr = Array.isArray(value);
  var keys = isObj ? Object.keys(value) : [];
  var indent = depth * 14;
  var displayLabel = getNodeTreeLabel(label, isArr);

  if (!isObj) {
    return h('button', {
      className: 'pp-row pp-row-leaf',
      style: { paddingLeft: indent + 'px' },
      onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onSelect(path); }
    },
      h('span', { className: 'pp-toggle-spacer' }),
      label != null ? h('span', { className: 'pp-key' }, displayLabel) : null,
      label != null ? h('span', { className: 'pp-separator' }, ':') : null,
      h('span', { className: 'pp-val' }, summarizeVal(value))
    );
  }

  if (label == null && depth === 0) {
    return h('div', { className: 'pp-group' },
      keys.map(function(k) {
        return h(NodePropPickerNode, {
          key: k, label: k, value: value[k],
          path: path ? path + '.' + k : k,
          depth: depth + 1, onSelect: onSelect
        });
      })
    );
  }

  return h('div', { className: 'pp-group' },
    h('div', { className: 'pp-row pp-row-branch', style: { paddingLeft: indent + 'px' } },
      h('button', {
        type: 'button',
        className: 'pp-toggle-btn',
        onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); setOpen(function(v) { return !v; }); }
      }, open ? '-' : '+'),
      h('button', {
        className: 'pp-select-btn',
        onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onSelect(path); }
      },
        h('span', { className: 'pp-key' }, displayLabel),
        h('span', { className: 'pp-preview' }, getNodeTreeSummary(isArr, keys.length))
      )
    ),
    open ? keys.map(function(k) {
      return h(NodePropPickerNode, {
        key: k, label: k, value: value[k],
        path: path ? path + '.' + k : k,
        depth: depth + 1, onSelect: onSelect
      });
    }) : null
  );
}

function NodePropPicker(props) {
  var output = props.output;
  var onSelect = props.onSelect;
  var onClose = props.onClose;
  var position = props.position;
  var pickerRoot = getAutoExpandedPickerRoot(output);
  var rootValue = pickerRoot.value;
  var rootPathPrefix = pickerRoot.pathPrefix;
  var isArr = Array.isArray(rootValue);
  var isObj = !isArr && rootValue !== null && rootValue !== undefined && typeof rootValue === 'object';

  var content = h('div', {
    className: 'prop-picker-balloon',
    style: { position: 'fixed', left: position.left + 'px', top: position.top + 'px', zIndex: 9999 },
    onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); }
  },
    h('div', { className: 'prop-picker-header' },
      h('span', { className: 'prop-picker-title' }, 'Choose value'),
      h('button', { className: 'prop-picker-close', onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onClose(); } }, '\u00d7')
    ),
    h('div', { className: 'prop-picker-list' },
      h('button', {
        className: 'pp-row pp-row-full',
        onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onSelect(null); }
      },
        h('span', { className: 'pp-toggle-spacer' }),
        h('span', { className: 'pp-key pp-key-full' }, 'Full output'),
        h('span', { className: 'pp-val' },
          isArr ? rootValue.length + ' items' : (isObj ? Object.keys(rootValue).length + ' fields' : summarizeVal(rootValue))
        )
      ),
      (isArr || isObj) ? h(NodePropPickerNode, { label: null, value: rootValue, path: rootPathPrefix, depth: 0, onSelect: onSelect }) : null
    )
  );

  return ReactDOM.createPortal(content, document.body);
}

function collectObjectPropPaths(value, maxDepth, maxPaths) {
  var depthLimit = Number.isFinite(maxDepth) ? maxDepth : 3;
  var pathLimit = Number.isFinite(maxPaths) ? maxPaths : 24;
  var out = [];
  var seen = [];

  function walk(current, path, depth) {
    if (out.length >= pathLimit) return;
    if (current === null || current === undefined) return;
    if (typeof current !== 'object') return;
    if (depth > depthLimit) return;
    if (seen.indexOf(current) >= 0) return;
    seen.push(current);

    if (Array.isArray(current)) {
      if (!current.length) return;
      var arrItemPath = path ? path + '.0' : '0';
      walk(current[0], arrItemPath, depth + 1);
      return;
    }

    var keys = Object.keys(current);
    for (var i = 0; i < keys.length; i += 1) {
      if (out.length >= pathLimit) break;
      var key = keys[i];
      var nextPath = path ? path + '.' + key : key;
      out.push(nextPath);
      walk(current[key], nextPath, depth + 1);
    }
  }

  walk(value, '', 0);
  return out;
}

function FieldTreeRow(props) {
  var label = props.label;
  var value = props.value;
  var path = props.path;
  var depth = props.depth || 0;
  var onSelect = props.onSelect;
  var [open, setOpen] = useState(depth < 1);

  var isArr = Array.isArray(value);
  var isObj = !isArr && value !== null && value !== undefined && typeof value === 'object';
  var isExpandable = isArr || isObj;
  var childKeys = isObj
    ? Object.keys(value).slice(0, 30)
    : (isArr && value.length > 0 ? Object.keys(value[0] || {}).slice(0, 30) : []);

  function preview() {
    if (value === null || value === undefined) return 'null';
    if (isArr) return '[ ' + value.length + ' ]';
    if (isObj) return '{ ' + Object.keys(value).length + ' }';
    var s = String(value);
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
      var fmt = formatDateTimeDisplay(s);
      if (fmt) return fmt;
    }
    return s.length > 28 ? s.slice(0, 28) + '\u2026' : s;
  }

  return h('div', null,
    h('div', {
      className: 'sf-row',
      style: { paddingLeft: (4 + depth * 14) + 'px' }
    },
      h('button', {
        className: 'sf-expand-btn',
        style: { visibility: isExpandable ? 'visible' : 'hidden' },
        onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); setOpen(function(v) { return !v; }); }
      }, open ? '\u25be' : '\u25b8'),
      h('span', {
        className: 'sf-field',
        onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onSelect(path); }
      },
        label ? h('span', { className: 'sf-key' }, label + ':') : null,
        ' ',
        h('span', { className: 'sf-preview sf-preview-' + (isArr ? 'arr' : isObj ? 'obj' : 'val') }, preview())
      )
    ),
    isExpandable && open
      ? childKeys.map(function(k) {
          var childVal = isObj ? value[k] : (value[0] ? value[0][k] : undefined);
          var childPath = isObj
            ? (path ? path + '.' + k : k)
            : (path ? path + '.0.' + k : '0.' + k);
          return h(FieldTreeRow, {
            key: k, label: k, value: childVal,
            path: childPath,
            depth: depth + 1, onSelect: onSelect
          });
        })
      : null
  );
}

function FieldSelectorPanel(props) {
  var vn = props.vn;
  var onSelect = props.onSelect;
  var onBack = props.onBack;
  var output = vn._runtimeOutput;
  var isArr = Array.isArray(output);
  var isObj = !isArr && output !== null && output !== undefined && typeof output === 'object';
  var isComplex = isArr || isObj;
  var rootKeys = isObj ? Object.keys(output) : null;
  var rootLen = isArr ? output.length : null;

  return h('div', { className: 'sf-panel', onMouseDown: function(e) { e.stopPropagation(); } },
    h('div', { className: 'sf-panel-head' },
      h('button', { className: 'sf-back-btn', onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onBack(); } }, '\u2190 Back'),
      h('span', { className: 'sf-panel-title' }, 'SELECT FIELD')
    ),
    h('div', { className: 'sf-panel-body' },
      h('div', {
        className: 'sf-row sf-row-full',
        onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); onSelect(null); }
      },
        h('span', { className: 'sf-full-out' }, 'Full output'),
        rootKeys !== null
          ? h('span', { className: 'sf-count-badge' }, '{' + rootKeys.length + ' keys}')
          : rootLen !== null
          ? h('span', { className: 'sf-count-badge' }, '[' + rootLen + ' items]')
          : null
      ),
      isComplex
        ? h('div', { className: 'sf-tree-wrap' },
            isArr
              ? (output.length > 0
                  ? Object.keys(output[0] || {}).slice(0, 30).map(function(k) {
                      return h(FieldTreeRow, {
                        key: k, label: k, value: output[0][k],
                        path: '0.' + k, depth: 0, onSelect: onSelect
                      });
                    })
                  : h('div', { className: 'sf-empty' }, 'Empty array'))
              : rootKeys.map(function(k) {
                  return h(FieldTreeRow, {
                    key: k, label: k, value: output[k],
                    path: k, depth: 0, onSelect: onSelect
                  });
                })
          )
        : h('div', { className: 'sf-empty' }, 'No structured data')
    )
  );
}

// ── Inline DateTime Picker ────────────────────────────────────────────────────
var MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
var DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function parseDateTimeValue(val) {
  if (!val) return null;
  var d = new Date(val);
  if (isNaN(d.getTime())) return null;
  return d;
}

function formatDateTimeValue(d) {
  if (!d) return '';
  var y = d.getFullYear();
  var m = String(d.getMonth() + 1).padStart(2, '0');
  var day = String(d.getDate()).padStart(2, '0');
  var hr = String(d.getHours()).padStart(2, '0');
  var min = String(d.getMinutes()).padStart(2, '0');
  return y + '-' + m + '-' + day + 'T' + hr + ':' + min;
}

function formatDateTimeDisplay(val) {
  var d = parseDateTimeValue(val);
  if (!d) return val || '';
  var m = MONTH_NAMES[d.getMonth()];
  var day = d.getDate();
  var y = d.getFullYear();
  var hr = d.getHours();
  var ampm = hr >= 12 ? 'PM' : 'AM';
  hr = hr % 12 || 12;
  var min = String(d.getMinutes()).padStart(2, '0');
  return m + ' ' + day + ', ' + y + ' ' + hr + ':' + min + ' ' + ampm;
}

function DateTimePicker(props) {
  var value = props.value;
  var onChange = props.onChange;
  var onCommit = props.onCommit;
  var onUseVariable = props.onUseVariable;

  var now = new Date();
  var parsed = parseDateTimeValue(value);
  var initYear = parsed ? parsed.getFullYear() : now.getFullYear();
  var initMonth = parsed ? parsed.getMonth() : now.getMonth();

  var _vy = useState(initYear); var viewYear = _vy[0]; var setViewYear = _vy[1];
  var _vm = useState(initMonth); var viewMonth = _vm[0]; var setViewMonth = _vm[1];

  var selDate = parsed || now;
  var _hr = useState(parsed ? parsed.getHours() : now.getHours()); var hours = _hr[0]; var setHours = _hr[1];
  var _mn = useState(parsed ? parsed.getMinutes() : now.getMinutes()); var minutes = _mn[0]; var setMinutes = _mn[1];

  function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
  function firstDayOfMonth(y, m) { return new Date(y, m, 1).getDay(); }

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else { setViewMonth(viewMonth - 1); }
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else { setViewMonth(viewMonth + 1); }
  }

  function selectDay(day) {
    var d = new Date(viewYear, viewMonth, day, hours, minutes);
    onChange(formatDateTimeValue(d));
  }

  function updateTime(newHours, newMinutes) {
    setHours(newHours);
    setMinutes(newMinutes);
    var d = new Date(selDate.getFullYear(), selDate.getMonth(), selDate.getDate(), newHours, newMinutes);
    onChange(formatDateTimeValue(d));
  }

  var totalDays = daysInMonth(viewYear, viewMonth);
  var startDay = firstDayOfMonth(viewYear, viewMonth);
  var cells = [];
  for (var b = 0; b < startDay; b++) cells.push(null);
  for (var d = 1; d <= totalDays; d++) cells.push(d);

  var isSelectedDay = function(day) {
    return parsed && parsed.getFullYear() === viewYear && parsed.getMonth() === viewMonth && parsed.getDate() === day;
  };
  var isToday = function(day) {
    return now.getFullYear() === viewYear && now.getMonth() === viewMonth && now.getDate() === day;
  };

  return h('div', {
    className: 'wf-datetime-picker',
    onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); }
  },
    // Header
    h('div', { className: 'wf-datetime-header' },
      h('button', { type: 'button', className: 'wf-datetime-nav', onClick: prevMonth }, '\u2039'),
      h('span', { className: 'wf-datetime-title' }, MONTH_NAMES[viewMonth] + ' ' + viewYear),
      h('button', { type: 'button', className: 'wf-datetime-nav', onClick: nextMonth }, '\u203A')
    ),
    // Day names
    h('div', { className: 'wf-datetime-weekdays' },
      DAY_NAMES.map(function(dn) { return h('span', { key: dn, className: 'wf-datetime-weekday' }, dn); })
    ),
    // Calendar grid
    h('div', { className: 'wf-datetime-grid' },
      cells.map(function(day, idx) {
        if (day === null) return h('span', { key: 'e' + idx, className: 'wf-datetime-cell wf-datetime-cell-empty' });
        var cls = 'wf-datetime-cell';
        if (isSelectedDay(day)) cls += ' wf-datetime-cell-selected';
        else if (isToday(day)) cls += ' wf-datetime-cell-today';
        return h('span', {
          key: day,
          className: cls,
          onClick: function() { selectDay(day); }
        }, day);
      })
    ),
    // Time row
    h('div', { className: 'wf-datetime-time' },
      h('input', {
        type: 'number', className: 'wf-datetime-time-input', min: 0, max: 23,
        value: String(hours).padStart(2, '0'),
        onChange: function(e) { var v = Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)); updateTime(v, minutes); },
        onMouseDown: function(e) { e.stopPropagation(); }
      }),
      h('span', { className: 'wf-datetime-time-sep' }, ':'),
      h('input', {
        type: 'number', className: 'wf-datetime-time-input', min: 0, max: 59,
        value: String(minutes).padStart(2, '0'),
        onChange: function(e) { var v = Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0)); updateTime(hours, v); },
        onMouseDown: function(e) { e.stopPropagation(); }
      })
    ),
    // Actions
    h('div', { className: 'wf-datetime-actions' },
      h('button', {
        type: 'button', className: 'wf-datetime-btn wf-datetime-btn-now',
        onClick: function() {
          var n = new Date();
          setViewYear(n.getFullYear()); setViewMonth(n.getMonth());
          setHours(n.getHours()); setMinutes(n.getMinutes());
          onChange(formatDateTimeValue(n));
        }
      }, 'Now'),
      h('button', {
        type: 'button', className: 'wf-datetime-btn wf-datetime-btn-ok',
        onClick: function() { onCommit(); }
      }, 'OK')
    ),
    onUseVariable
      ? h('button', {
          type: 'button', className: 'wf-datetime-var-link',
          onClick: onUseVariable
        }, '@ Use variable')
      : null
  );
}

function HyperParamChips(props) {
  var params = props.params;
  var paramValues = props.paramValues || {};
  var onParamChange = props.onParamChange || function() {};
  var variableNodes = props.variableNodes || [];
  var nodeStatus = props.nodeStatus || null;
  var nodeCallCount = props.nodeCallCount || 0;

  var [editingKey, setEditingKey] = useState(null);
  var [expanded, setExpanded] = useState(false);
  var [draft, setDraft] = useState('');
  var [arrayDraft, setArrayDraft] = useState([]);
  var [selectedIdx, setSelectedIdx] = useState(0);
  var [showDatePicker, setShowDatePicker] = useState(false);
  var [fieldSelectorVn, setFieldSelectorVn] = useState(null);
  var [fieldSelectorPos, setFieldSelectorPos] = useState(null);
  var inputRef = useRef(null);
  var dropdownRef = useRef(null);
  var editingKeyRef = useRef(editingKey);
  var draftRef = useRef(draft);
  var arrayDraftRef = useRef(arrayDraft);

  // Keep latest values in refs without adding conditional hooks.
  editingKeyRef.current = editingKey;
  draftRef.current = draft;
  arrayDraftRef.current = arrayDraft;

  if (!Array.isArray(params) || params.length === 0) return null;

  // Compute variable suggestions based on draft.
  // Compatible types are ranked first, but incompatible ones are still shown
  // so users can always discover variables.
  var showDropdown = false;
  var suggestions = [];
  if (editingKey !== null && typeof draft === 'string') {
    var editingParam = params.find(function(p) { return p.key === editingKey; });
    var paramType = editingParam ? editingParam.type : null;
    var isArrayParam = !!(editingParam && Array.isArray(paramValues[editingParam.key]));

    function isCompatibleType(varDataType) {
      if (!paramType || paramType === 'any') return true;
      var vt = normalizeVariableType(varDataType);
      if (paramType === 'number') return vt === 'int';
      if (paramType === 'boolean') return vt === 'bool';
      if (paramType === 'string') return vt === 'string' || vt === 'date';
      if (paramType === 'datetime') return vt === 'date';
      return true;
    }

    function sortByCompatibility(a, b) {
      var aType = a && a.paramValues ? a.paramValues.dataType : null;
      var bType = b && b.paramValues ? b.paramValues.dataType : null;
      var aScore = isCompatibleType(aType) ? 1 : 0;
      var bScore = isCompatibleType(bType) ? 1 : 0;
      if (aScore !== bScore) return bScore - aScore;
      var aName = String((a && a.title) || '').toLowerCase();
      var bName = String((b && b.title) || '').toLowerCase();
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return 0;
    }

    var atIdx = draft.lastIndexOf('@');
    var query = isArrayParam
      ? String(draft || '').trim().toLowerCase()
      : (atIdx >= 0 ? draft.slice(atIdx + 1).toLowerCase() : '');
    suggestions = variableNodes.filter(function(vn) {
      var name = String(vn.title || '').trim();
      var suffix = vn && vn._isNodeOutput ? '' : '';
      var ref = '@' + name + suffix;
      if (!name.length) return false;
      if (isArrayParam && arrayDraftRef.current.indexOf(ref) >= 0) return false;
      return isArrayParam
        ? name.toLowerCase().indexOf(query) >= 0
        : (atIdx >= 0 ? name.toLowerCase().indexOf(query) >= 0 : true);
    }).sort(sortByCompatibility);

    showDropdown = isArrayParam ? suggestions.length > 0 : suggestions.length > 0;
  }

  function selectVariable(vn, selectedPath) {
    var name = String(vn.title || '').trim();
    var suffix = selectedPath ? '.' + selectedPath : '';
    var refValue = '@' + name + suffix;
    var editingParam = params.find(function(p) { return p.key === editingKeyRef.current; });
    var isArrayParam = !!(editingParam && Array.isArray(paramValues[editingParam.key]));

    if (isArrayParam) {
      setArrayDraft(function(prev) {
        if (prev.indexOf(refValue) >= 0) return prev;
        var next = prev.concat([refValue]);
        onParamChange(editingKeyRef.current, next);
        return next;
      });
      setDraft('');
      setSelectedIdx(0);
      setFieldSelectorVn(null);
      setFieldSelectorPos(null);
      setTimeout(function() {
        if (inputRef.current) inputRef.current.focus();
      }, 0);
      return;
    }

    var atIdx = draft.lastIndexOf('@');
    var newDraft = atIdx >= 0
      ? draft.slice(0, atIdx) + refValue
      : refValue;
    setDraft(newDraft);
    setSelectedIdx(0);
    setFieldSelectorVn(null);
    setFieldSelectorPos(null);
    // Commit immediately after selecting
    if (editingKeyRef.current !== null) {
      onParamChange(editingKeyRef.current, newDraft);
    }
    setEditingKey(null);
  }

  function startEdit(p, e) {
    e.stopPropagation();
    e.preventDefault();
    // Commit any pending edit before switching to the new param
    if (editingKeyRef.current !== null && editingKeyRef.current !== p.key) {
      var prevKey = editingKeyRef.current;
      var prevParam = params.find(function(pp) { return pp.key === prevKey; });
      var isPrevArray = !!(prevParam && Array.isArray(paramValues[prevParam.key]));
      var prevValue = isPrevArray ? arrayDraftRef.current : draftRef.current;
      onParamChange(prevKey, prevValue);
    }
    editingKeyRef.current = p.key;
    var rawVal = paramValues[p.key] !== undefined ? paramValues[p.key] : p.default;
    var isArrayParam = Array.isArray(rawVal);
    var val = isArrayParam ? '' : String(rawVal || '');
    setEditingKey(p.key);
    setDraft(val);
    setArrayDraft(isArrayParam ? rawVal.map(function(item) { return String(item || '').trim(); }).filter(Boolean) : []);
    setSelectedIdx(0);
    if (p.type === 'datetime') {
      setShowDatePicker(true);
    } else {
      setTimeout(function() {
        if (inputRef.current) {
          inputRef.current.focus();
          if (!isArrayParam && val.indexOf('@') === 0 && val.length > 1) inputRef.current.select();
        }
      }, 0);
    }
  }

  function commitEdit(commitKey, commitValue) {
    var targetKey = commitKey !== undefined && commitKey !== null ? commitKey : editingKeyRef.current;
    // If startEdit already switched to a different param, it already committed this edit — skip.
    if (targetKey !== null && editingKeyRef.current !== null && editingKeyRef.current !== targetKey) {
      return;
    }
    var editingParam = params.find(function(p) { return p.key === targetKey; });
    var isArrayParam = !!(editingParam && Array.isArray(paramValues[editingParam.key]));
    var targetValue = isArrayParam
      ? arrayDraftRef.current
      : (commitValue !== undefined ? commitValue : draftRef.current);
    if (targetKey !== null) {
      onParamChange(targetKey, targetValue);
    }
    setEditingKey(null);
    editingKeyRef.current = null;
    setShowDatePicker(false);
    setFieldSelectorVn(null);
    setFieldSelectorPos(null);
    setSelectedIdx(0);
    setDraft('');
  }

  function handleKeyDown(e) {
    e.stopPropagation();
    var editingParam = params.find(function(p) { return p.key === editingKeyRef.current; });
    var isArrayParam = !!(editingParam && Array.isArray(paramValues[editingParam.key]));
    if (fieldSelectorVn) {
      if (e.key === 'Escape') { e.preventDefault(); setFieldSelectorVn(null); setFieldSelectorPos(null); }
      return;
    }
    if (showDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx(function(prev) { return Math.min(prev + 1, suggestions.length - 1); });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx(function(prev) { return Math.max(prev - 1, 0); });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectVariable(suggestions[selectedIdx]);
        return;
      }
    }
    if (isArrayParam && e.key === 'Backspace' && !draftRef.current && arrayDraftRef.current.length > 0) {
      e.preventDefault();
      setArrayDraft(function(prev) {
        var next = prev.slice(0, -1);
        onParamChange(editingKeyRef.current, next);
        return next;
      });
      return;
    }
    if (e.key === 'Enter') commitEdit(editingKeyRef.current, draftRef.current);
    if (e.key === 'Escape') { setEditingKey(null); setSelectedIdx(0); }
  }

  var visible = expanded ? params : params.slice(0, 4);
  var overflow = expanded ? 0 : params.length - visible.length;

  return h('div', { className: 'wf-node-params' },
    visible.map(function(p, i) {
      var rawCurrentVal = paramValues[p.key] !== undefined ? paramValues[p.key] : p.default;
      var isArrayValue = Array.isArray(rawCurrentVal);
      var currentVal = isArrayValue
        ? rawCurrentVal.join(',')
        : (rawCurrentVal !== undefined && rawCurrentVal !== null ? String(rawCurrentVal) : '');
      var isEditing = editingKey === p.key;
      var isVarRef = currentVal.indexOf('@') === 0 && currentVal.length > 1;
      var arrayItems = isArrayValue
        ? rawCurrentVal.map(function(item) {
            return String(item || '').trim();
          }).filter(Boolean)
        : [];

      if (isEditing) {
        var isArrayEditing = isArrayValue;
        return h('span', { key: p.id || i, className: 'wf-node-param-chip wf-node-param-chip-editing', style: { position: 'relative' } },
          h('span', { className: 'wf-node-param-key' }, p.key),
          h('span', { className: 'wf-node-param-sep' }, '='),
          isArrayEditing
            ? h('span', { className: 'wf-node-param-input-array' },
                arrayDraft.map(function(item) {
                  return h('span', {
                    key: item,
                    className: 'wf-node-param-list-chip' + (item.indexOf('@') === 0 ? ' wf-node-param-list-chip-var' : '')
                  },
                    item,
                    h('button', {
                      type: 'button',
                      className: 'wf-node-param-list-chip-remove',
                      onMouseDown: function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        setArrayDraft(function(prev) {
                          var next = prev.filter(function(entry) { return entry !== item; });
                          onParamChange(p.key, next);
                          return next;
                        });
                      }
                    }, '\u00d7')
                  );
                }),
                h('input', {
                  ref: inputRef,
                  className: 'wf-node-param-input wf-node-param-input-inline',
                  type: 'text',
                  value: draft,
                  placeholder: 'Add variable or output',
                  onChange: function(e) { setDraft(e.target.value); setSelectedIdx(0); },
                  onBlur: function() {
                    setTimeout(function() {
                      commitEdit(p.key);
                    }, 120);
                  },
                  onKeyDown: handleKeyDown,
                  onMouseDown: function(e) { e.stopPropagation(); }
                })
              )
            : p.type === 'boolean'
            ? h('select', {
                ref: inputRef,
                className: 'wf-node-param-input',
                value: draft,
                onChange: function(e) { setDraft(e.target.value); },
                onBlur: function(e) { commitEdit(p.key, e.target.value); },
                onKeyDown: handleKeyDown,
                onMouseDown: function(e) { e.stopPropagation(); }
              },
                h('option', { value: 'true' }, 'true'),
                h('option', { value: 'false' }, 'false')
              )
            : p.type === 'datetime'
            ? h('span', { className: 'wf-node-param-datetime-wrap' },
                h('span', {
                  className: 'wf-node-param-datetime-display',
                  onClick: function() { setShowDatePicker(!showDatePicker); }
                }, formatDateTimeDisplay(draft) || 'pick date\u2026'),
                showDatePicker
                  ? h(DateTimePicker, {
                      value: draft,
                      onChange: function(v) { setDraft(v); },
                      onCommit: function() { setShowDatePicker(false); commitEdit(p.key, draftRef.current); },
                      onUseVariable: function() {
                        setShowDatePicker(false);
                        setDraft('');
                        setTimeout(function() {
                          if (inputRef.current) inputRef.current.focus();
                        }, 0);
                      }
                    })
                  : null,
                !showDatePicker && editingKey === p.key
                  ? h('input', {
                      ref: inputRef,
                      className: 'wf-node-param-input wf-node-param-datetime-var',
                      type: 'text',
                      value: draft.indexOf('@') === 0 ? draft : '',
                      placeholder: '@var',
                      onChange: function(e) { setDraft(e.target.value); setSelectedIdx(0); },
                      onBlur: function(e) {
                        setTimeout(function() { commitEdit(p.key, e.target.value || draftRef.current); }, 150);
                      },
                      onKeyDown: handleKeyDown,
                      onMouseDown: function(e) { e.stopPropagation(); },
                      style: { position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'hidden' }
                    })
                  : null
              )
            : h('input', {
                ref: inputRef,
                className: 'wf-node-param-input',
                // Keep text input for most params so @variable refs work
                // even on number-typed hyperparameters.
                type: 'text',
                inputMode: p.type === 'number' ? 'decimal' : undefined,
                value: draft,
                placeholder: 'value or select variable',
                onChange: function(e) { setDraft(e.target.value); setSelectedIdx(0); },
                onBlur: function(e) { commitEdit(p.key, e.target.value); },
                onKeyDown: handleKeyDown,
                onMouseDown: function(e) { e.stopPropagation(); }
              }),
          showDropdown && !fieldSelectorVn && !showDatePicker
            ? h('div', { ref: dropdownRef, className: 'wf-var-dropdown', onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); } },
                (function() {
                  var varItems = suggestions.filter(function(vn) { return !vn._isNodeOutput; });
                  var outItems = suggestions.filter(function(vn) { return vn._isNodeOutput; });
                  var items = [];
                  var si = 0;
                  if (varItems.length) {
                    items.push(h('div', { key: '__hdr-var', className: 'wf-var-dropdown-section' }, 'Variables'));
                    varItems.forEach(function(vn) {
                      var idx = si++;
                      var varParams = vn.paramValues || {};
                      var varType = VAR_TYPE_SHORT[normalizeVariableType(varParams.dataType)] || 'str';
                      var varRuntimeOutput = vn._runtimeOutput;
                      var varHasComplex = varRuntimeOutput !== null && varRuntimeOutput !== undefined && typeof varRuntimeOutput === 'object';
                      var varOutputSummary = varHasComplex
                        ? (Array.isArray(varRuntimeOutput)
                            ? varRuntimeOutput.length + ' items'
                            : Object.keys(varRuntimeOutput).length + ' fields')
                        : '';
                      items.push(h('div', {
                        key: vn.id,
                        className: 'wf-var-dropdown-item' + (idx === selectedIdx ? ' wf-var-dropdown-item-active' : ''),
                        onMouseDown: function(e) {
                          e.preventDefault(); e.stopPropagation();
                          if (varHasComplex) {
                            var rect = e.currentTarget.getBoundingClientRect();
                            setFieldSelectorPos(pickerPositionFromRect(rect));
                            setFieldSelectorVn(vn);
                          } else {
                            selectVariable(vn);
                          }
                        }
                      },
                        h('span', { className: 'wf-var-dropdown-at' }, '@'),
                        h('span', { className: 'wf-var-dropdown-name' }, vn.title),
                        varOutputSummary ? h('span', { className: 'wf-var-dropdown-meta' }, varOutputSummary) : null,
                        h('span', { className: 'wf-var-dropdown-type' + (varHasComplex ? ' wf-var-dropdown-type-out' : '') }, varHasComplex ? 'data' : varType),
                        varHasComplex ? h('span', { className: 'wf-var-dropdown-expand' }, '\u203a') : null
                      ));
                    });
                  }
                  if (outItems.length) {
                    items.push(h('div', { key: '__hdr-out', className: 'wf-var-dropdown-section' + (varItems.length ? ' wf-var-dropdown-section-border' : '') }, 'From previous steps'));
                    outItems.forEach(function(vn) {
                      var idx = si++;
                      var outputValue = vn && Object.prototype.hasOwnProperty.call(vn, '_runtimeOutput') ? vn._runtimeOutput : null;
                      var hasComplexOutput = outputValue !== null && outputValue !== undefined && typeof outputValue === 'object';
                      var outputSummary = hasComplexOutput
                        ? (Array.isArray(outputValue)
                            ? outputValue.length + ' items'
                            : Object.keys(outputValue).length + ' fields')
                        : '';
                      items.push(h('div', {
                        key: vn.id,
                        className: 'wf-var-dropdown-item' + (idx === selectedIdx ? ' wf-var-dropdown-item-active' : ''),
                        onMouseDown: function(e) {
                          e.preventDefault(); e.stopPropagation();
                          if (hasComplexOutput) {
                            var rect = e.currentTarget.getBoundingClientRect();
                            setFieldSelectorPos(pickerPositionFromRect(rect));
                            setFieldSelectorVn(vn);
                          } else {
                            selectVariable(vn);
                          }
                        }
                      },
                        h('span', { className: 'wf-var-dropdown-at' }, '@'),
                        h('span', { className: 'wf-var-dropdown-name' }, vn.title),
                        outputSummary ? h('span', { className: 'wf-var-dropdown-meta' }, outputSummary) : null,
                        h('span', { className: 'wf-var-dropdown-type wf-var-dropdown-type-out' }, hasComplexOutput ? 'data' : 'value'),
                        hasComplexOutput ? h('span', { className: 'wf-var-dropdown-expand' }, '\u203a') : null
                      ));
                    });
                  }
                  return items;
                })()
              )
            : null
        );
      }

      // Detect N++ / N-- pattern and resolve its live counter value (shared
      // with the sidebar param input via getIncrementPreview).
      var runtimeDisplayVal = getIncrementPreview(currentVal, nodeCallCount, nodeStatus);
      var hasRuntimeVal = runtimeDisplayVal !== null;

      return h('button', {
        key: p.id || i,
        className: 'wf-node-param-chip wf-node-param-chip-btn' + (isVarRef ? ' wf-node-param-chip-var' : '') + (arrayItems.length ? ' wf-node-param-chip-array' : '') + (hasRuntimeVal ? ' wf-node-param-chip-runtime' : ''),
        title: hasRuntimeVal ? ('Current: ' + runtimeDisplayVal + ' (click to edit: ' + currentVal + ')') : 'Click to edit',
        onMouseDown: function(e) { startEdit(p, e); }
      },
        h('span', { className: 'wf-node-param-key' }, p.key),
        h('span', { className: 'wf-node-param-sep' }, '='),
        arrayItems.length > 0
              ? h('span', { className: 'wf-node-param-list', title: arrayItems.join(', ') },
                  arrayItems.slice(0, 2).map(function(item) {
                    return h('span', {
                      key: item,
                      className: 'wf-node-param-list-chip' + (item.indexOf('@') === 0 ? ' wf-node-param-list-chip-var' : '')
                    }, item);
                  }),
                  arrayItems.length > 2
                    ? h('span', { className: 'wf-node-param-list-more' }, '+' + (arrayItems.length - 2))
                    : null
              )
          : isVarRef
          ? h('span', { className: 'wf-node-param-var' }, currentVal)
          : hasRuntimeVal
            ? h('span', { className: 'wf-node-param-val wf-node-param-val-runtime' }, runtimeDisplayVal)
          : currentVal !== ''
            ? h('span', { className: 'wf-node-param-val' }, p.type === 'datetime' ? formatDateTimeDisplay(currentVal) || currentVal : currentVal)
            : h('span', { className: 'wf-node-param-empty' }, TYPE_SHORT[p.type] || p.type)
      );
    }),
    overflow > 0
      ? h('button', {
          className: 'wf-node-param-chip wf-node-param-more wf-node-param-chip-btn',
          title: 'Show all parameters',
          onMouseDown: function(e) { e.stopPropagation(); e.preventDefault(); setExpanded(true); }
        }, '+' + overflow)
      : null,
    expanded && params.length > 4
      ? h('button', {
          className: 'wf-node-param-chip wf-node-param-more wf-node-param-chip-btn',
          title: 'Show less',
          onMouseDown: function(e) { e.stopPropagation(); e.preventDefault(); setExpanded(false); }
        }, '\u2212')
      : null,
    fieldSelectorVn && fieldSelectorPos
      ? h(NodePropPicker, {
          output: fieldSelectorVn._runtimeOutput,
          position: fieldSelectorPos,
          onSelect: function(path) {
            selectVariable(fieldSelectorVn, path !== null && path !== undefined ? path : undefined);
            setFieldSelectorPos(null);
          },
          onClose: function() {
            setFieldSelectorVn(null);
            setFieldSelectorPos(null);
            setEditingKey(null);
          }
        })
      : null
  );
}

function WorkflowNode(props) {
  var type = props.type;
  var title = props.title === 'Variables' && props.icon === 'variables' ? 'Variable' : props.title;
  var displayTitle = props.displayTitle;
  var subtitle = props.subtitle;
  var icon = props.icon;
  var logoUrl = props.logoUrl;
  var options = props.options;
  var stats = props.stats;
  var borderColor = props.borderColor;
  var hyperparameters = props.hyperparameters;
  var paramValues = props.paramValues || {};
  var conditionRule = props.conditionRule || null;
  var runtimeOutput = props.runtimeOutput;
  var variableNodes = props.variableNodes || [];
  var readOnly = !!props.readOnly;
  var showDetails = props.showDetails !== false;
  var detailPreviewActive = !!props.detailPreviewActive;
  var onParamChange = props.onParamChange || function() {};
  var onConditionValueChange = props.onConditionValueChange || function() {};
  var onTitleChange = props.onTitleChange || function() {};
  var onMenuClick = props.onMenuClick || null;
  var isExpanded = !!props.isExpanded;
  var onNodeHover = props.onNodeHover || null;
  var isConditionNode = icon === 'condition';
  var isVariableNode = icon === 'variables';
  var isSetVariableNode = icon === 'set-variable';
  // Control / utility steps render as the smaller 92px tile. BOTH the inner tile
  // and the outer wrap must shrink — the wrap stays 132px otherwise, and the
  // connector handles (anchored to the wrap's edge) float off to the side.
  // Tool nodes (custom nodes wired to a Tool Agent's bottom anchor) render as
  // the smaller tile in a distinct color so they read as sub-tools, not main
  // flow steps. `isToolNode` is computed by the canvas from the connections.
  var isToolNode = props.isToolNode || false;
  var isCompactTile = isSetVariableNode || isConditionNode || isToolNode;
  var isConsoleLogNode = icon === 'console-log';
  var hasExplicitWidth = props.hasExplicitWidth || false;
  var isDragging = props.isDragging || false;
  var nodeStatus = props.nodeStatus || null;
  var nodeCallCount = props.nodeCallCount || 0;
  var isWorkflowRunning = props.isWorkflowRunning || false;
  var eyeLookDir = props.eyeLookDir || null;
  var isAgentNode = icon === 'agent' && props.category !== 'custom';
  var isAiCustomNode = icon === 'agent' && props.category === 'custom';
  var isToolAgentNode = icon === 'tool-agent';
  // Tool Agent shares the AI-node affordances (the click-to-cycle tier badge).
  var isAnyAgentNode = isAgentNode || isAiCustomNode || isToolAgentNode;
  var isClassifierNode = icon === 'classifier';
  var isRouterNode = icon === 'router';
  var isLoopNode = icon === 'loop';
  var isSleepNode = icon === 'sleep';
  var isStartNode = icon === 'start';
  var isFilePreviewNode = icon === 'file-preview';
  var isInlineTitleEditable = type !== 'user' && !isStartNode && !readOnly;
  var titleEditState = useState(false);
  var isTitleEditing = titleEditState[0];
  var setIsTitleEditing = titleEditState[1];
  var titleInputRef = useRef(null);
  var headerTitle = (isSetVariableNode || isConditionNode) && String(displayTitle || '').trim()
    ? String(displayTitle || '').trim()
    : title;
  var systemObjectTheme = getSystemObjectTheme(icon);
  var iconStyle = systemObjectTheme ? { background: systemObjectTheme.bg, color: systemObjectTheme.color } : null;

  function getOptionClass(option) {
    var value = String(option || '').toLowerCase();
    if (value === 'yes') return ' wf-node-option-pill-yes';
    if (value === 'no') return ' wf-node-option-pill-no';
    return '';
  }

  if (type === 'user') {
    return h('div', { className: 'wf-node wf-node-user' },
      h('div', { className: 'wf-node-icon wf-node-icon-user wf-node-icon-type-' + (icon || 'user') },
        h(NodeIcon, { type: icon })
      ),
      h('div', { className: 'wf-node-info' },
        h('div', { className: 'wf-node-title' }, title)
      )
    );
  }

  if (isStartNode) {
    return h('div', { className: 'wf-node wf-node-start-pill' },
      h('div', { className: 'wf-node-start-icon', style: iconStyle },
        h(NodeIcon, { type: icon, size: 22 })
      ),
      h('div', { className: 'wf-node-start-label' }, title || 'Start'),
      !readOnly && !!onMenuClick
        ? h('button', {
            className: 'wf-node-menu wf-node-start-menu',
            onMouseDown: function(event) {
              event.preventDefault();
              event.stopPropagation();
              if (onMenuClick) onMenuClick();
            },
            title: 'Start parameters'
          },
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'currentColor' },
              h('circle', { cx: 12, cy: 5, r: 2 }),
              h('circle', { cx: 12, cy: 12, r: 2 }),
              h('circle', { cx: 12, cy: 19, r: 2 })
            )
          )
        : null
    );
  }

  // Hooks for the card-mode (Router/Classifier) condition editor. These must be
  // declared before the early return below so React's hook order stays stable
  // even if a node's icon type changes (which would otherwise flip rendering paths).
  var [editingConditionId, setEditingConditionId] = useState(null);
  var [conditionDraft, setConditionDraft] = useState('');
  var [condVarIdx, setCondVarIdx] = useState(0);
  var [condFieldSelectorVn, setCondFieldSelectorVn] = useState(null);
  var [condFieldSelectorPos, setCondFieldSelectorPos] = useState(null);
  var [condFieldSelectorParsed, setCondFieldSelectorParsed] = useState(null);
  var conditionInputRef = useRef(null);

  // Only Router and Classifier keep the wide card layout — they render rich
  // routing/classification UIs that don't fit a tile. Every other node type
  // (including condition / variable / set-variable / agent / loop / sleep /
  // console-log / file-preview / generic action nodes) renders as a compact
  // pastel tile with a smart subtitle that surfaces its key inline data.
  var isSpecialNode = isClassifierNode || isRouterNode;

  if (!isSpecialNode) {
    var tileSubtitle = getNodeSubtitle({
      icon: icon,
      category: props.category,
      subtitle: subtitle,
      paramValues: paramValues,
      conditionRule: conditionRule
    });
    var titleSlot = null;
    if (isInlineTitleEditable && isTitleEditing) {
      titleSlot = h('input', {
        ref: titleInputRef,
        className: 'wf-node-tile-title-input',
        type: 'text',
        value: title || '',
        autoFocus: true,
        onChange: function(event) { onTitleChange(event.target.value); },
        onMouseDown: function(event) { event.stopPropagation(); },
        onBlur: function() { setIsTitleEditing(false); },
        onKeyDown: function(event) {
          if (event.key === 'Enter' || event.key === 'Escape') {
            setIsTitleEditing(false);
            event.target.blur();
          }
        }
      });
    } else if (isInlineTitleEditable) {
      titleSlot = h('span', {
        className: 'wf-node-tile-title-text',
        onDoubleClick: function(event) {
          event.stopPropagation();
          setIsTitleEditing(true);
        }
      }, title || '');
    }

    return h('div', {
      className: 'wf-node wf-node-tile-wrap'
        + (readOnly ? ' wf-node-readonly' : '')
        + (isCompactTile ? ' wf-node-tile-wrap-compact' : '')
        + (isToolNode ? ' wf-node-tile-wrap-tool' : '')
        + (nodeStatus ? ' wf-node-tile-status-' + nodeStatus : ''),
      // For dependent (read-only duplicate) tiles the dashed border already
      // signals the state visually — surface the explanation via a tooltip
      // so we don't need an extra badge cluttering the tile.
      title: readOnly ? 'Dependent duplicate node. Unlock to edit independently.' : undefined
    },
      h(NodeTile, {
        icon: icon,
        logoUrl: logoUrl,
        title: title,
        subtitle: tileSubtitle,
        isDragging: isDragging,
        nodeStatus: nodeStatus,
        isWorkflowRunning: isWorkflowRunning,
        eyeLookDir: eyeLookDir,
        titleSlot: titleSlot,
        glyphOverlay: isAnyAgentNode && !readOnly
          ? h(AiTierCycleButton, { paramValues: paramValues, onParamChange: onParamChange, toolAgent: isToolAgentNode })
          : null,
        // Set Variable and Condition are tiny control/utility steps ("set X to
        // Y", "is X > 0?"), not primary actions — render them as smaller tiles so
        // the canvas reads them as sub-steps next to full-size action tiles.
        compact: isCompactTile,
        // Tool nodes get a distinct color family so they read as the agent's
        // sub-tools, not main flow steps.
        colorFamily: isToolNode ? 'tool' : null,
        // Tool nodes are agent nodes but keep a changeable logo (picked via the
        // logo picker) — normal agent nodes stay locked to the robot glyph.
        allowAgentLogo: isToolNode,
        onLogoEdit: (!readOnly && (icon === 'custom' || isToolNode) && props.onLogoDoubleClick)
          ? function() { props.onLogoDoubleClick(); }
          : null
      }),
      !readOnly && !!onMenuClick
        ? h('button', {
            className: 'wf-node-menu wf-node-tile-menu',
            onMouseDown: function(event) {
              event.preventDefault();
              event.stopPropagation();
              if (onMenuClick) onMenuClick();
            },
            title: 'Node menu'
          },
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'currentColor' },
              h('circle', { cx: 12, cy: 5, r: 2 }),
              h('circle', { cx: 12, cy: 12, r: 2 }),
              h('circle', { cx: 12, cy: 19, r: 2 })
            )
          )
        : null
    );
  }

  // Variable suggestions for condition value editing
  var condShowDropdown = false;
  var condSuggestions = [];
  if (editingConditionId !== null && typeof conditionDraft === 'string') {
    var condAtIdx = conditionDraft.lastIndexOf('@');
    if (condAtIdx >= 0) {
      var condQuery = conditionDraft.slice(condAtIdx + 1).toLowerCase();
      condSuggestions = variableNodes.filter(function(vn) {
        var name = String(vn.title || '').trim();
        return name.length > 0 && name.toLowerCase().indexOf(condQuery) >= 0;
      });
      condShowDropdown = condSuggestions.length > 0;
    }
  }

  function selectCondVariable(vn, fieldPath) {
    var name = String(vn.title || '').trim();
    var atIdx = conditionDraft.lastIndexOf('@');
    var suffix = fieldPath ? '.' + fieldPath : '';
    var newDraft = conditionDraft.slice(0, atIdx) + '@' + name + suffix;
    setConditionDraft(newDraft);
    setCondVarIdx(0);
    onConditionValueChange(firstCondition.id, newDraft);
    setEditingConditionId(null);
    setCondFieldSelectorVn(null);
    setCondFieldSelectorPos(null);
    setCondFieldSelectorParsed(null);
  }

  var cardStyle = borderColor ? { borderColor: borderColor } : {};
  var normalizedRule = normalizeConditionRule(conditionRule);
  var allConditions = normalizedRule.conditions;
  var firstCondition = allConditions[0] || null;
  var activeConditions = allConditions.filter(function(item) {
    return conditionHasEffectiveValue(item);
  });
  var conditionOperator = OPERATOR_LABELS[firstCondition && firstCondition.operator] || '=';
  var hasConditionRule = hasAnyConditionValue(conditionRule);
  var extraCount = Math.max(0, activeConditions.length - 1);
  var logicLabel = String(normalizedRule.logic || 'and').toUpperCase();

  var expandableNode = isConditionNode || isSetVariableNode;

  return h('div', { className: 'wf-node wf-node-card' + (isVariableNode ? ' wf-node-card-variable' : '') + (isSetVariableNode ? ' wf-node-card-setvar' : '') + (isConditionNode ? ' wf-node-card-condition' : '') + (hasExplicitWidth ? ' wf-node-card-resized' : '') + (isAgentNode ? ' wf-node-card-agent' : '') + (isClassifierNode ? ' wf-node-card-classifier' : '') + (isRouterNode ? ' wf-node-card-classifier' : '') + (!showDetails ? ' wf-node-card-compact' : '') + (detailPreviewActive ? ' wf-node-card-compact-preview' : '') + (readOnly ? ' wf-node-readonly' : '') + (expandableNode && isExpanded ? ' wf-node-card-expanded' : ''), style: cardStyle, onMouseEnter: expandableNode && onNodeHover ? onNodeHover : undefined },
    isAnyAgentNode && !readOnly
      ? h(AiTierCycleButton, { paramValues: paramValues, onParamChange: onParamChange, toolAgent: isToolAgentNode })
      : null,
    h('div', { className: 'wf-node-card-header' },
      !isVariableNode
        ? h('div', {
            className: 'wf-node-icon wf-node-icon-card wf-node-icon-type-' + (icon || 'custom') + (logoUrl && (!isAgentNode || isToolNode) ? ' wf-node-icon-has-logo' : ''),
            style: iconStyle
          },
            // Tool nodes are agent nodes but keep a changeable logo; plain
            // agent nodes stay locked to the robot glyph.
            logoUrl && (!isAgentNode || isToolNode)
              ? h('img', { src: logoUrl, alt: title, className: 'wf-node-logo', onError: function(e) { e.target.style.display = 'none'; } })
              : h(NodeIcon, { type: icon, isDragging: isDragging, nodeStatus: nodeStatus, isWorkflowRunning: isWorkflowRunning, eyeLookDir: eyeLookDir }),
            ((icon === 'custom' || isToolNode) && !readOnly && props.onLogoDoubleClick)
              ? h('button', {
                  type: 'button',
                  className: 'wf-node-icon-edit-btn',
                  title: 'Change image',
                  'aria-label': 'Change image',
                  onMouseDown: function(e) { e.stopPropagation(); },
                  onClick: function(e) { e.stopPropagation(); props.onLogoDoubleClick(); }
                },
                  h('svg', { width: 11, height: 11, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                    h('path', { d: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z' }),
                    h('circle', { cx: 12, cy: 13, r: 4 })
                  )
                )
              : null
          )
        : null,
      h('div', { className: 'wf-node-info' },
        ((isSetVariableNode || isConditionNode) && String(displayTitle || '').trim())
          ? h('div', { className: 'wf-node-title wf-node-title-compact', title: headerTitle }, headerTitle)
          : isInlineTitleEditable
          ? (isTitleEditing
            ? h('input', {
                ref: titleInputRef,
                className: 'wf-node-title-input',
                type: 'text',
                value: title || '',
                size: Math.max(10, (title || '').length + 2),
                autoFocus: true,
                onChange: function(event) { onTitleChange(event.target.value); },
                onMouseDown: function(event) { event.stopPropagation(); },
                onBlur: function() { setIsTitleEditing(false); },
                onKeyDown: function(event) {
                  if (event.key === 'Enter' || event.key === 'Escape') {
                    setIsTitleEditing(false);
                    event.target.blur();
                  }
                }
              })
            : h('div', {
                className: 'wf-node-title',
                onDoubleClick: function(event) {
                  event.stopPropagation();
                  setIsTitleEditing(true);
                }
              }, title))
          : h('div', { className: 'wf-node-title' }, title),
        readOnly
          ? h('div', {
              className: 'wf-node-dependent-tag',
              title: 'Dependent duplicate node. Unlock to edit independently.'
            }, 'Dependent')
          : null
      ),
      !isVariableNode && !readOnly && !!onMenuClick
        ? h('button', {
            className: 'wf-node-menu',
            onMouseDown: function(event) {
              event.preventDefault();
              event.stopPropagation();
              if (onMenuClick) onMenuClick();
            },
            onTouchEnd: function(event) {
              event.preventDefault();
              event.stopPropagation();
              if (onMenuClick) onMenuClick();
            },
            title: isConditionNode ? 'Condition settings' : (isAgentNode ? 'AI settings' : (isClassifierNode ? 'Classifier settings' : (isRouterNode ? 'Router settings' : (isStartNode ? 'Start parameters' : 'Node menu'))))
          },
            h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'currentColor' },
              h('circle', { cx: 12, cy: 5, r: 2 }),
              h('circle', { cx: 12, cy: 12, r: 2 }),
              h('circle', { cx: 12, cy: 19, r: 2 })
            )
          )
        : null
    ),
    isConditionNode
      ? h('div', { className: 'wf-node-condition-summary' },
          Array.isArray(hyperparameters) && hyperparameters.length > 0
            ? h(HyperParamChips, { params: hyperparameters.map(function(p) { return Object.assign({}, p, { type: 'any' }); }), paramValues: paramValues, onParamChange: onParamChange, variableNodes: variableNodes })
            : null,
          h('div', { className: 'wf-node-condition-rule' },
            h('span', { className: 'wf-node-condition-token wf-node-condition-token-op' }, conditionOperator),
            firstCondition && operatorRequiresValue(firstCondition.operator)
              ? (editingConditionId === firstCondition.id
                  ? h('span', { className: 'wf-node-condition-token wf-node-condition-token-value wf-node-condition-token-editing', style: { position: 'relative' } },
                      h('input', {
                        ref: conditionInputRef,
                        className: 'wf-node-condition-input',
                        type: 'text',
                        value: conditionDraft,
                        placeholder: 'value or select variable',
                        onChange: function(e) { setConditionDraft(e.target.value); setCondVarIdx(0); },
                        onBlur: function() {
                          setTimeout(function() {
                            onConditionValueChange(firstCondition.id, conditionDraft);
                            setEditingConditionId(null);
                          }, 150);
                        },
                        onKeyDown: function(e) {
                          e.stopPropagation();
                          if (condShowDropdown) {
                            if (e.key === 'ArrowDown') { e.preventDefault(); setCondVarIdx(function(p) { return Math.min(p + 1, condSuggestions.length - 1); }); return; }
                            if (e.key === 'ArrowUp') { e.preventDefault(); setCondVarIdx(function(p) { return Math.max(p - 1, 0); }); return; }
                            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectCondVariable(condSuggestions[condVarIdx]); return; }
                          }
                          if (e.key === 'Enter') {
                            onConditionValueChange(firstCondition.id, conditionDraft);
                            setEditingConditionId(null);
                          }
                          if (e.key === 'Escape') setEditingConditionId(null);
                        },
                        onMouseDown: function(e) { e.stopPropagation(); }
                      }),
                      condShowDropdown
                        ? h('div', { className: 'wf-var-dropdown', onMouseDown: function(e) { e.preventDefault(); e.stopPropagation(); } },
                            (function() {
                              var varItems = condSuggestions.filter(function(vn) { return !vn._isNodeOutput; });
                              var outItems = condSuggestions.filter(function(vn) { return vn._isNodeOutput; });
                              var items = [];
                              var si = 0;
                              if (varItems.length) {
                                items.push(h('div', { key: '__hdr-var', className: 'wf-var-dropdown-section' }, 'Variables'));
                                varItems.forEach(function(vn) {
                                  var idx = si++;
                                  var varParams = vn.paramValues || {};
                                  var normalizedDataType = normalizeVariableType(varParams.dataType);
                                  var varType = VAR_TYPE_SHORT[normalizedDataType] || 'str';
                                  var isComplexVar = normalizedDataType === 'json' || normalizedDataType === 'array';
                                  var parsedVarValue = null;
                                  if (isComplexVar) {
                                    try { parsedVarValue = JSON.parse(String(varParams.value || '')); } catch(e) {}
                                  }
                                  var hasComplexValue = parsedVarValue !== null && parsedVarValue !== undefined && typeof parsedVarValue === 'object';
                                  items.push(h('div', {
                                    key: vn.id,
                                    className: 'wf-var-dropdown-item' + (idx === condVarIdx ? ' wf-var-dropdown-item-active' : ''),
                                    onMouseDown: function(e) {
                                      e.preventDefault(); e.stopPropagation();
                                      if (hasComplexValue) {
                                        var rect = e.currentTarget.getBoundingClientRect();
                                        setCondFieldSelectorVn(vn);
                                        setCondFieldSelectorPos(pickerPositionFromRect(rect));
                                        setCondFieldSelectorParsed(parsedVarValue);
                                      } else {
                                        selectCondVariable(vn);
                                      }
                                    }
                                  },
                                    h('span', { className: 'wf-var-dropdown-at' }, '@'),
                                    h('span', { className: 'wf-var-dropdown-name' }, vn.title),
                                    h('span', { className: 'wf-var-dropdown-type' }, varType),
                                    hasComplexValue ? h('span', { className: 'wf-var-dropdown-expand' }, '\u203a') : null
                                  ));
                                });
                              }
                              if (outItems.length) {
                                items.push(h('div', { key: '__hdr-out', className: 'wf-var-dropdown-section' + (varItems.length ? ' wf-var-dropdown-section-border' : '') }, 'From previous steps'));
                                outItems.forEach(function(vn) {
                                  var idx = si++;
                                  var outputValue = vn && Object.prototype.hasOwnProperty.call(vn, '_runtimeOutput') ? vn._runtimeOutput : null;
                                  var hasComplexOutput = outputValue !== null && outputValue !== undefined && typeof outputValue === 'object';
                                  var outputSummary = hasComplexOutput
                                    ? (Array.isArray(outputValue)
                                        ? outputValue.length + ' items'
                                        : Object.keys(outputValue).length + ' fields')
                                    : '';
                                  items.push(h('div', {
                                    key: vn.id,
                                    className: 'wf-var-dropdown-item' + (idx === condVarIdx ? ' wf-var-dropdown-item-active' : ''),
                                    onMouseDown: function(e) {
                                      e.preventDefault(); e.stopPropagation();
                                      if (hasComplexOutput) {
                                        var rect = e.currentTarget.getBoundingClientRect();
                                        setCondFieldSelectorVn(vn);
                                        setCondFieldSelectorPos(pickerPositionFromRect(rect));
                                        setCondFieldSelectorParsed(outputValue);
                                      } else {
                                        selectCondVariable(vn);
                                      }
                                    }
                                  },
                                    h('span', { className: 'wf-var-dropdown-at' }, '@'),
                                    h('span', { className: 'wf-var-dropdown-name' }, vn.title),
                                    outputSummary ? h('span', { className: 'wf-var-dropdown-meta' }, outputSummary) : null,
                                    h('span', { className: 'wf-var-dropdown-type wf-var-dropdown-type-out' }, hasComplexOutput ? 'data' : 'value'),
                                    hasComplexOutput ? h('span', { className: 'wf-var-dropdown-expand' }, '\u203a') : null
                                  ));
                                });
                              }
                              return items;
                            })()
                          )
                        : null
                    )
                  : h('span', {
                      className: 'wf-node-condition-token wf-node-condition-token-value wf-node-condition-token-clickable',
                      onMouseDown: function(e) {
                        e.stopPropagation();
                        e.preventDefault();
                        setEditingConditionId(firstCondition.id);
                        setConditionDraft(String(firstCondition.value || ''));
                        setTimeout(function() { if (conditionInputRef.current) conditionInputRef.current.focus(); }, 0);
                      }
                    }, String(firstCondition.value || '') || 'click to set')
                )
              : null,
            extraCount > 0
              ? h('span', { className: 'wf-node-condition-token wf-node-condition-token-more' }, '+' + extraCount + ' (' + logicLabel + ')')
              : null
          )
        )
      : null,
    condFieldSelectorVn && condFieldSelectorPos
      ? h(NodePropPicker, {
          output: condFieldSelectorParsed,
          position: condFieldSelectorPos,
          onSelect: function(path) {
            selectCondVariable(condFieldSelectorVn, path !== null && path !== undefined ? path : undefined);
          },
          onClose: function() {
            setCondFieldSelectorVn(null);
            setCondFieldSelectorPos(null);
            setCondFieldSelectorParsed(null);
          }
        })
      : null,
    isVariableNode
      ? h(VariableEditor, { paramValues: paramValues, onParamChange: onParamChange, runtimeValue: runtimeOutput })
      : null,
    isAgentNode
      ? h(AgentSummary, { paramValues: paramValues })
      : null,
    isClassifierNode
      ? h(ClassifierSummary, { paramValues: paramValues, runtimeOutput: runtimeOutput, onParamChange: onParamChange })
      : null,
    isRouterNode
      ? h(RouterSummary, { paramValues: paramValues, runtimeOutput: runtimeOutput, onParamChange: onParamChange, variableNodes: variableNodes })
      : null,
    isConsoleLogNode
      ? h(ConsoleLogOutput, { paramValues: paramValues, runtimeOutput: runtimeOutput })
      : null,
    isSetVariableNode
      ? h(SetVariableEditor, { paramValues: paramValues, onParamChange: onParamChange, variableNodes: variableNodes })
      : null,
    isLoopNode
      ? h('div', { className: 'wf-node-condition-chips' },
          h('div', { className: 'wf-node-condition-chip-row' },
            h('span', { className: 'wf-node-condition-chip wf-node-condition-chip-op' }, 'each item'),
            h('span', { className: 'wf-node-condition-chip wf-node-condition-chip-op' }, 'done')
          )
        )
      : null,
    isSleepNode
      ? h('div', { className: 'wf-node-condition-chips' },
          h('div', { className: 'wf-node-condition-chip-row' },
            h('span', { className: 'wf-node-condition-chip wf-node-condition-chip-var' },
              (function() {
                var dur = paramValues.duration !== undefined ? paramValues.duration : 1000;
                var unit = String(paramValues.unit || 'ms');
                return String(dur) + ' ' + unit;
              })()
            )
          )
        )
      : null,
    !isConditionNode && !isVariableNode && !isSetVariableNode && !isAgentNode && !isClassifierNode && !isRouterNode && !isConsoleLogNode && !isLoopNode && !isSleepNode && h(HyperParamChips, { params: hyperparameters, paramValues: paramValues, onParamChange: onParamChange, variableNodes: variableNodes, nodeStatus: nodeStatus, nodeCallCount: nodeCallCount }),
    !isConditionNode && !isVariableNode && !isSetVariableNode && !isAgentNode && !isClassifierNode && !isRouterNode && !isConsoleLogNode && !isLoopNode && !isSleepNode && Array.isArray(options) && options.length
      ? h('div', { className: 'wf-node-options' },
          options.map(function(option, i) {
            return h('span', { key: i, className: 'wf-node-option-pill' + getOptionClass(option) }, option);
          })
        )
      : null,
    isFilePreviewNode
      ? h(FilePreviewDisplay, { runtimeOutput: runtimeOutput })
      : null,
    !isConditionNode && !isVariableNode && !isAgentNode && !isClassifierNode && !isRouterNode && !isConsoleLogNode && stats ? h('div', { className: 'wf-node-stats' },
      stats.map(function(stat, i) {
        var className = 'wf-stat' + (stat.color ? ' wf-stat-' + stat.color : '');
        return h('div', { key: i, className: className },
          h(StatIcon, { type: stat.icon }),
          h('span', null, stat.value)
        );
      })
    ) : null
  );
}

module.exports = WorkflowNode;
// Re-export the inline editors so the right sidebar params panel can render
// the same UIs that used to live inside the wider node card. Keeping a single
// definition avoids duplication and keeps both render targets in sync.
module.exports.HyperParamChips = HyperParamChips;
module.exports.SetVariableEditor = SetVariableEditor;
module.exports.VariableEditor = VariableEditor;
module.exports.ConsoleLogOutput = ConsoleLogOutput;
module.exports.FilePreviewDisplay = FilePreviewDisplay;
module.exports.AgentSummary = AgentSummary;
